import { AuthenticatedUser, ISchedule } from "@gettactic/api";
import { isPast } from "date-fns";
import { canScheduleOthers } from "../permissions";
import { findCheckinTargetSlot } from "./getCurrentUserCanEdit";

/**
 * Whether the signed-in user may **cancel** this reservation.
 *
 * Deliberately not {@link getCurrentUserCanEdit}: the API reports editing and
 * cancelling separately, and a reservation that is already under way comes
 * back with `can_edit: false` but `can_respond_cancelled: true` — you can drop
 * today's desk, you just can't move it. Gating cancellation on `can_edit`
 * therefore locks exactly the booking someone leaving early most wants to
 * release.
 *
 * The ownership rule matches the edit helper: a meeting room may only be
 * cancelled by its organizer (or someone who schedules for others), because an
 * attendee cancelling would drop the whole booking for everyone.
 */
export function getCurrentUserCanCancel(
  schedule: ISchedule,
  authenticatedUser: AuthenticatedUser
): boolean {
  // Walk-up check-ins have no end until checkout — a missing end means still
  // ongoing, not "in the past".
  const endDate = schedule.end ? new Date(schedule.end.replace("Z", "")) : null;
  if (endDate && isPast(endDate)) {
    return false;
  }

  const targetSlot = findCheckinTargetSlot(
    schedule,
    authenticatedUser.user?.id ?? ""
  );
  if (!targetSlot) {
    return false;
  }

  if (!targetSlot.capabilities?.can_respond_cancelled) {
    return false;
  }

  const isOrganizer = schedule.organizer_id === authenticatedUser.user?.id;
  const resourceType = (targetSlot.resource ?? schedule.resource)?.type;

  return (
    isOrganizer ||
    canScheduleOthers(authenticatedUser) ||
    resourceType !== "meeting_room"
  );
}
